import { useState, useRef, useCallback, useEffect } from "react";
import { t } from "../i18n";

/**
 * usePlayback – Manages timeline playback, preview polling, and segment interaction.
 *
 * @param {object} options
 * @param {object} options.montage – The current montage state.
 * @param {function} options.setMontage – Setter for montage state.
 * @param {React.MutableRefObject} options.lastSavedRef – Ref tracking last serialized save.
 * @param {function} options.toast – Toast notification helper.
 * @returns {object} Playback state and handlers.
 */
export default function usePlayback({ montage, setMontage, lastSavedRef, toast }) {
    // Playback state
    const [currentTimeMs, setCurrentTimeMs] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [activeSegmentId, setActiveSegmentId] = useState(null);
    const playbackTimerRef = useRef(null);

    // Preview state
    const previewPollRef = useRef(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewAutoPlay, setPreviewAutoPlay] = useState(false);
    const [timelineData, setTimelineData] = useState(null);

    // Transition editing
    const [editingTransition, setEditingTransition] = useState(null);

    // Derived preview values
    const previewData = montage?.json_data?.preview_video || {};
    const previewUrl = previewData?.url || "";
    const previewStatus = previewData?.status || "";
    const previewEnabled = previewStatus === "completed" && !!previewUrl;
    const allVideosReady = montage.json_data.segments.length > 0 && montage.json_data.segments.every(
        (seg) => seg.video?.status === "completed" && seg.video?.url
    );

    // ─── Fetch timeline data (frames + waveform) when videos are ready ───
    useEffect(() => {
        if (!allVideosReady || !montage.montage_id) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(`/api/fast/montage/${montage.montage_id}/timeline-data`);
                const data = await res.json();
                if (!cancelled && data?.ok) {
                    setTimelineData(data);
                }
            } catch {
                // Non-critical — timeline will fallback to segment images
            }
        })();
        return () => { cancelled = true; };
    }, [allVideosReady, montage.montage_id]);

    // ─── Sync previewLoading with previewStatus ───
    useEffect(() => {
        if (previewStatus === "generating" && !previewLoading) {
            setPreviewLoading(true);
        }
        if (previewStatus !== "generating" && previewLoading && previewEnabled) {
            setPreviewLoading(false);
        }
    }, [previewEnabled, previewLoading, previewStatus]);

    // ─── Playback timer ───
    useEffect(() => {
        if (!isPlaying || previewEnabled) {
            if (playbackTimerRef.current) {
                clearInterval(playbackTimerRef.current);
                playbackTimerRef.current = null;
            }
            return;
        }

        const totalDurationMs = montage.json_data.segments.reduce(
            (sum, seg) => sum + (seg.duration || 0) * 1000,
            0
        );

        playbackTimerRef.current = setInterval(() => {
            setCurrentTimeMs(prev => {
                const next = prev + 100;
                if (next >= totalDurationMs) {
                    setIsPlaying(false);
                    return 0;
                }
                return next;
            });
        }, 100);

        return () => {
            if (playbackTimerRef.current) {
                clearInterval(playbackTimerRef.current);
            }
        };
    }, [isPlaying, montage.json_data.segments, previewEnabled]);

    // ─── Preview polling ───
    useEffect(() => {
        if (!previewLoading || !montage.montage_id) {
            if (previewPollRef.current) {
                clearInterval(previewPollRef.current);
                previewPollRef.current = null;
            }
            return;
        }

        previewPollRef.current = setInterval(async () => {
            try {
                const res = await fetch(`/api/fast/montage/${montage.montage_id}`);
                const data = await res.json();
                if (!data?.ok || !data?.montage) return;
                setMontage(data.montage);
                lastSavedRef.current = JSON.stringify(data.montage);
                const nextPreview = data.montage?.json_data?.preview_video || {};
                if (nextPreview.status === "completed" && nextPreview.url) {
                    setPreviewLoading(false);
                    if (previewAutoPlay) {
                        setIsPlaying(true);
                        setPreviewAutoPlay(false);
                    }
                }
                if (nextPreview.status === "error") {
                    setPreviewLoading(false);
                    setPreviewAutoPlay(false);
                    toast(nextPreview.error || t("montage.err.preview"), "error");
                }
            } catch {
                // Ignore transient polling errors.
            }
        }, 1500);

        return () => {
            if (previewPollRef.current) {
                clearInterval(previewPollRef.current);
                previewPollRef.current = null;
            }
        };
    }, [montage.montage_id, previewAutoPlay, previewLoading, setMontage, lastSavedRef, toast]);

    // ─── Play / Pause handler ───
    const handlePlayPause = useCallback(() => {
        if (previewEnabled) {
            setIsPlaying(prev => !prev);
            return;
        }
        if (previewLoading || previewStatus === "generating") {
            return;
        }
        if (allVideosReady) {
            setIsPlaying(false);
            setPreviewAutoPlay(true);
            setPreviewLoading(true);
            fetch(`/api/fast/montage/${montage.montage_id}/preview`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ resolution: "360p" })
            })
                .then(res => res.json())
                .then(data => {
                    if (data?.ok && data?.montage) {
                        setMontage(data.montage);
                        lastSavedRef.current = JSON.stringify(data.montage);
                    } else {
                        setPreviewLoading(false);
                        setPreviewAutoPlay(false);
                        toast(data?.error || t("montage.err.preview"), "error");
                    }
                })
                .catch(() => {
                    setPreviewLoading(false);
                    setPreviewAutoPlay(false);
                    toast(t("montage.err.preview"), "error");
                });
            return;
        }
        setIsPlaying(prev => !prev);
    }, [allVideosReady, montage.montage_id, previewEnabled, previewLoading, previewStatus, setMontage, lastSavedRef, toast]);

    const handleSeek = useCallback((timeMs) => {
        setCurrentTimeMs(timeMs);
    }, []);

    // ─── Segment interaction ───
    const handleSegmentClick = useCallback((segment) => {
        setActiveSegmentId(segment.segment_id);
    }, []);

    const handleTransitionClick = useCallback((fromIndex, toIndex) => {
        setEditingTransition({ fromIndex, toIndex });
    }, []);

    const handleSaveTransition = useCallback((transitionData) => {
        const { fromSegmentId, toSegmentId, transitionOut, transitionIn } = transitionData;

        setMontage(prev => {
            const newSegments = [...prev.json_data.segments];

            const fromIdx = newSegments.findIndex(s => s.segment_id === fromSegmentId);
            if (fromIdx >= 0) {
                newSegments[fromIdx] = { ...newSegments[fromIdx], transition_out: transitionOut };
            }

            const toIdx = newSegments.findIndex(s => s.segment_id === toSegmentId);
            if (toIdx >= 0) {
                newSegments[toIdx] = { ...newSegments[toIdx], transition_in: transitionIn };
            }

            return {
                ...prev,
                json_data: { ...prev.json_data, segments: newSegments }
            };
        });

        setEditingTransition(null);
    }, [setMontage]);

    // ─── Segment reorder ───
    const handleSegmentReorder = useCallback((fromIndex, toIndex) => {
        setMontage(prev => {
            const newSegments = [...prev.json_data.segments];
            const [movedSegment] = newSegments.splice(fromIndex, 1);
            newSegments.splice(toIndex, 0, movedSegment);
            newSegments.forEach((seg, idx) => { seg.order = idx + 1; });

            return {
                ...prev,
                json_data: { ...prev.json_data, segments: newSegments }
            };
        });
    }, [setMontage]);

    return {
        // State
        currentTimeMs,
        setCurrentTimeMs,
        isPlaying,
        setIsPlaying,
        activeSegmentId,
        setActiveSegmentId,
        previewLoading,
        previewAutoPlay,
        timelineData,
        editingTransition,
        setEditingTransition,

        // Derived
        previewUrl,
        previewStatus,
        previewEnabled,
        allVideosReady,

        // Handlers
        handlePlayPause,
        handleSeek,
        handleSegmentClick,
        handleTransitionClick,
        handleSaveTransition,
        handleSegmentReorder,
    };
}
